import React from 'react'
import { Link } from 'react-router-dom'
import { Users } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useGetMatchPreviewQuery } from '@/redux/marketerApi'

/**
 * B1: how many athletes on Athios fit what the brand is looking for, live as they pick. Shows a
 * few of them so the number feels real.
 */
export const MatchPreview: React.FC<{
  sports: number[]
  states: string[]
  gender: string
}> = ({ sports, states, gender }) => {
  const { data, isFetching } = useGetMatchPreviewQuery({ sports, states, gender })
  const { data: everyone } = useGetMatchPreviewQuery({ sports: [], states: [], gender: 'any' })
  const narrowed = sports.length > 0 || gender !== 'any'
  const count = data?.count ?? 0

  return (
    <section className="rounded-xl border border-foreground/10 bg-card p-5 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600">
        <Users className="h-4 w-4" />
        Your match
      </p>
      <p className="mt-2 text-4xl font-bold tabular-nums text-foreground">
        {isFetching && !data ? '…' : count.toLocaleString()}
      </p>
      <p className="text-sm text-gray-600">
        {narrowed
          ? count === 1
            ? 'athlete on Athios fits what you picked.'
            : 'athletes on Athios fit what you picked.'
          : 'athletes on Athios. Pick sports and places to narrow it.'}
        {everyone && narrowed && everyone.count > count
          ? ` ${everyone.count.toLocaleString()} in all.`
          : ''}
      </p>
      {states.length && data && data.inPlaces != null ? (
        <p className="mt-1 text-xs text-gray-500">
          {data.inPlaces.toLocaleString()} of them have told us they're in the places you picked.
          Most athletes add their address when they set up payouts.
        </p>
      ) : null}
      {data?.sample.length ? (
        <ul className="mt-4 space-y-2">
          {data.sample.slice(0, 4).map((a) => (
            <li key={a.id} className="flex items-center gap-3">
              <Avatar className="h-9 w-9 border border-foreground/10">
                <AvatarImage src={a.avatar ?? undefined} alt="" />
                <AvatarFallback className="bg-muted text-xs text-foreground">
                  {a.name
                    .split(' ')
                    .map((p) => p[0])
                    .join('')
                    .slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground">
                  {a.name}
                </span>
                <span className="block truncate text-xs text-gray-600">
                  {[a.sport, a.school].filter(Boolean).join(' · ')}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : narrowed && data ? (
        <p className="mt-3 text-sm text-gray-500">
          Nobody matches that yet. Widen the sports, or we'll tell you when someone joins.
        </p>
      ) : null}
      <Button asChild variant="dark-outline" size="sm" className="mt-4">
        <Link to="/marketplace">Shop athletes</Link>
      </Button>
    </section>
  )
}

export default MatchPreview
